import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import { makeStyles } from '@material-ui/core/styles'
import ActionsMenu from 'components/actions-menu'
import EventNoteOutlinedIcon from 'components/material-symbol/icons/EventNote'
import MessageIcon from 'components/material-symbol/icons/Message'
import MoreHorizIcon from 'components/material-symbol/icons/MoreHoriz'

const useStyles = makeStyles(theme => ({
  headerActions: {
    position: 'relative',
    display: 'flex',
    flex: '0 0 auto',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    minWidth: 0,
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'flex-start',
      width: '100%',
    },
  },
  headerActionsMeasure: {
    position: 'absolute',
    left: 0,
    top: 0,
    zIndex: -1,
    display: 'inline-flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    visibility: 'hidden',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
  },
  overflowButton: {
    minWidth: 40,
    width: 40,
    height: 40,
    padding: 0,
    borderRadius: '50px',
    border: '1px solid #E0E0E0',
    backgroundColor: '#FFFFFF',
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: 'none',
    flexShrink: 0,
    '&:hover': {
      backgroundColor: '#F5F5F5',
      borderColor: '#E0E0E0',
      boxShadow: 'none',
    },
  },
  menuIcon: {
    minWidth: 36,
    color: 'rgba(0, 0, 0, 0.54)',
  },
  actionButton: {
    flexShrink: 0,
  },
}))

const ClubDashboardHeaderActions = ({
  onBookClick,
  messagesPath,
  headerRowRef,
  titleBlockRef,
}) => {
  const classes = useStyles()
  const measureRef = useRef(null)
  const [collapseMessage, setCollapseMessage] = useState(false)

  const updateCollapse = useCallback(() => {
    const headerRow = headerRowRef?.current
    const titleBlock = titleBlockRef?.current
    const measure = measureRef.current
    if (!headerRow || !titleBlock || !measure) return

    const rowStyles = window.getComputedStyle(headerRow)
    const gap = Number.parseFloat(rowStyles.columnGap || rowStyles.gap || '0')
    const isStacked = rowStyles.flexDirection === 'column'
    const availableWidth = isStacked
      ? headerRow.clientWidth
      : headerRow.clientWidth - titleBlock.offsetWidth - gap

    setCollapseMessage(measure.scrollWidth > availableWidth)
  }, [headerRowRef, titleBlockRef])

  useLayoutEffect(() => {
    updateCollapse()

    const headerRow = headerRowRef?.current
    if (!headerRow) return undefined

    const resizeObserver = new ResizeObserver(updateCollapse)
    resizeObserver.observe(headerRow)
    if (titleBlockRef?.current) {
      resizeObserver.observe(titleBlockRef.current)
    }
    if (measureRef.current) {
      resizeObserver.observe(measureRef.current)
    }

    return () => resizeObserver.disconnect()
  }, [headerRowRef, titleBlockRef, updateCollapse])

  const bookButton = (
    <Button
      className={classes.actionButton}
      variant="contained"
      color="primary"
      startIcon={<EventNoteOutlinedIcon />}
      onClick={onBookClick}
    >
      Book Session
    </Button>
  )

  const messageButton = (
    <Button
      className={classes.actionButton}
      variant="outlined"
      component={RouterLink}
      to={messagesPath}
      startIcon={<MessageIcon />}
    >
      Message
    </Button>
  )

  const messageMenuItem = (
    <MenuItem component={RouterLink} to={messagesPath}>
      <ListItemIcon className={classes.menuIcon}>
        <MessageIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText primary="Message" />
    </MenuItem>
  )

  return (
    <Box className={classes.headerActions}>
      <Box
        ref={measureRef}
        className={classes.headerActionsMeasure}
        aria-hidden="true"
      >
        {bookButton}
        {messageButton}
      </Box>
      {bookButton}
      {collapseMessage ? (
        <ActionsMenu
          ariaId="club-dashboard-header"
          buttonContent={<MoreHorizIcon />}
          className={classes.overflowButton}
          color="default"
          variant="outlined"
        >
          {messageMenuItem}
        </ActionsMenu>
      ) : (
        messageButton
      )}
    </Box>
  )
}

export default ClubDashboardHeaderActions
